import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useFavouriteAthletes from "../../../hooks/useFavouriteAthletes";
import {
  faEllipsisVertical,
  faPen,
  faEye,
  faThumbsUp,
} from "@fortawesome/free-solid-svg-icons";

import Modal from "../../../components/Modal";
import AthleteScore from "../../../components/athlete-scores/athlete-score";
import CircularProgressBar from "../../../components/CircularProgressBar";
import LoadingSpinner from "../../../components/LoadingSpinner";
import AthleteProfileModal from "../../../components/AthleteProfileModal";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";

export default function AthleteChip({
  athlete,
  showStarButton,
  isAdmin = false,
  onAthleteUpdate,
}) {
  const [isAthleteModalOpen, setIsModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const authUser = useAuthUser();
  const isHighSchoolCoach = authUser?.user?.role === "highschool";
  const { getFavoriteState, handleFavoriteClick, likeLoadingById } =
    useFavouriteAthletes();

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreen = () => setIsMobile(window.innerWidth < 640);
    checkScreen();
    window.addEventListener("resize", checkScreen);
    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  const handleStarClick = async (event) => {
    if (likeLoadingById[athlete.userId]) return;

    await handleFavoriteClick(event, athlete, () => {
      toast.error("Error liking athlete", { position: "top-center" });
    });
  };
  const tier = athlete?.offerTier || "None";
  const offerAcceptanceStatus = athlete?.offerAcceptanceStatus;
  const showAcceptance =
    offerAcceptanceStatus === "Committed" || offerAcceptanceStatus === "Signed";
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getCircleBackgroundColor = () =>
    getFavoriteState(athlete) ? "bg-yellow-500" : "bg-gray-400";

  const handleViewClick = () => {
    setIsModalOpen(true);
  };

  const handleEditClick = () => {
    setIsProfileModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCloseProfileModal = () => {
    setIsProfileModalOpen(false);
  };

  return (
    <>
      <div
        className="flex justify-between items-center shadow rounded-lg border border-gray-200 px-3 py-2 mb-3 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex gap-0 w-full">
          <div className="h-[40px] w-[40px] my-auto overflow-hidden rounded-md">
            <img
              alt={`${athlete.name} ${athlete.lastname}`}
              src={athlete.profilePicture || "/default-profile-img.png"}
              className="grow-0 shrink-0 rounded-md h-[40px] w-[40px] object-cover"
            />
          </div>
          <div className="ml-3 w-7/12 xl:w-9/12">
            <p
              className="w-10/12 sm:w-auto text-base lg:text-lg uppercase truncate hover:underline hover:text-blue-700 cursor-pointer"
              onClick={handleViewClick}
            >
              {athlete.name} {athlete.lastname}
            </p>
            <p className="text-sm text-gray-700 line-clamp-1 ">
              {athlete.position || athlete.primaryPosition}
            </p>
          </div>

          <div className="flex items-center gap-1">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mr-2">
              {showAcceptance && (
                <span
                  className={`px-3 py-1 text-xs font-normal rounded-full sm:mr-1 ${
                    offerAcceptanceStatus === "Committed"
                      ? "bg-red-100 text-red-700"
                      : "bg-blue-100 text-blue-700"
                  }`}
                >
                  {offerAcceptanceStatus}
                </span>
              )}

              <span
                className={`px-3 py-1 text-xs font-normal rounded-full sm:mr-3 ${
                  tier === "None"
                    ? "bg-red-100 text-red-700"
                    : tier === "P4"
                      ? "bg-green-100 text-green-700"
                      : "bg-blue-100 text-blue-700"
                }`}
              >
                {tier}
              </span>
            </div>
            {showStarButton && !isHighSchoolCoach && (
              <button
                className="mr-4 text-[#8E8E93]"
                onClick={handleStarClick}
                type="button"
              >
                <div
                  className={`flex items-center justify-center h-[32px] w-[32px] rounded-full ${getCircleBackgroundColor()}`}
                >
                  {likeLoadingById[athlete.userId] ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <FontAwesomeIcon
                      icon={faThumbsUp}
                      style={{ color: "#fff" }}
                    />
                  )}
                </div>
              </button>
            )}

            <div className="">
              {isAdmin ? (
                <CircularProgressBar
                  progress={athlete.positionScore || 0}
                  size={isMobile ? 38 : 48}
                  strokeWidth={4}
                  color="#1C4ED8"
                />
              ) : (
                <CircularProgressBar
                  progress={athlete.positionScore || 0}
                  size={isMobile ? 38 : 48}
                  strokeWidth={4}
                  color="#1C4ED8"
                />
              )}
            </div>
            <div className="flex gap-2 ml-1">
              {/* Edit button - only visible for admin users */}
              {isAdmin && (
                <button
                  className="hidden lg:inline-flex text-lg px-2 py-2 info-icon cursor-pointer"
                  onClick={handleEditClick}
                >
                  <FontAwesomeIcon
                    icon={faPen}
                    className="text-lg text-gray-500 hover:text-gray-600"
                  />
                </button>
              )}
            </div>
            <div
              ref={dropdownRef}
              className="relative inline-block text-left lg:hidden"
            >
              {/* Trigger */}
              <button
                onClick={() => setOpen((prev) => !prev)}
                className="p-2 text-gray-500 text-lg"
              >
                <FontAwesomeIcon icon={faEllipsisVertical} />
              </button>

              {/* Dropdown */}
              {open && (
                <div className="absolute right-0 mt-2 w-40 bg-white border rounded-lg shadow-lg z-50">
                  <ul className="py-1 text-sm">
                    {/* View */}
                    <li
                      className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => {
                        handleViewClick();
                        setOpen(false);
                      }}
                    >
                      <FontAwesomeIcon icon={faEye} />
                      View
                    </li>

                    {/* Edit (ADMIN ONLY) */}
                    {isAdmin && (
                      <li
                        className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => {
                          handleEditClick();
                          setOpen(false);
                        }}
                      >
                        <FontAwesomeIcon icon={faPen} />
                        Edit
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Athlete Score Modal */}
      <Modal
        isOpen={isAthleteModalOpen}
        onClose={handleCloseModal}
        backdrop="static"
        keyboard={false}
      >
        <AthleteScore
          userId={athlete.userId}
          showOffers={true}
          showTrackittScore={true}
          showPositionScore={true}
          searchEvent={false}
          showProfile={true}
          showRank={true}
          showLikes={true}
        />
      </Modal>

      {/* Athlete Profile Edit Modal */}
      {isProfileModalOpen && (
        <AthleteProfileModal
          athlete={athlete}
          onClose={handleCloseProfileModal}
          onSave={onAthleteUpdate}
        />
      )}
    </>
  );
}
